import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Search, Loader2, CheckCircle2, Calendar, ChevronDown, ChevronUp, Mail, Phone, Plus, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mmh_admin_token";

const URGENCY_ORDER = {
  "In crisis — need to speak sooner": 0,
  "Feeling urgent": 1,
  "Would love to start soon": 2,
  "Ready when you are": 3,
};

const STATUS_STYLES = {
  new: "bg-[#D4A85B]/20 text-[#D4A85B] border-[#D4A85B]/40",
  scheduled: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  responded: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  closed: "bg-white/10 text-white/60 border-white/20",
};

const Field = ({ label, value }) => (
  value ? (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80">{label}</div>
      <div className="mt-1 text-sm text-[#EDE3D0]/90 leading-relaxed whitespace-pre-wrap break-words">{value}</div>
    </div>
  ) : null
);

const NotesEditor = ({ intakeId, initial, token, onSaved }) => {
  const [value, setValue] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setValue(initial || ""); }, [initial]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.patch(`${API}/intakes/${intakeId}/notes`, { private_notes: value }, { headers: { "x-admin-token": token } });
      setSaved(true);
      onSaved?.(value);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80">Private notes (only visible to you)</div>
        {saved && <span className="text-[10px] uppercase tracking-[0.24em] text-emerald-300">Saved</span>}
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        placeholder="Session prep, patterns to explore, follow-up prompts…"
        data-testid={`notes-${intakeId}`}
        className="w-full bg-[#0A0714]/60 border border-[#D4A85B]/25 focus:border-[#D4A85B] rounded-xl p-3 text-sm text-[#EDE3D0] placeholder-[#EDE3D0]/40 outline-none resize-none"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`notes-save-${intakeId}`}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#D4A85B] text-[#0A0714] px-4 py-2 text-[10px] uppercase tracking-[0.24em] hover:bg-[#E9C77B] transition disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save notes"}
      </button>
    </div>
  );
};

const FaqEditor = ({ token }) => {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`)
      .then((r) => setFaqs(r.data?.faqs || []))
      .finally(() => setLoading(false));
  }, []);

  const update = (i, key, val) => setFaqs((p) => p.map((f, idx) => idx === i ? { ...f, [key]: val } : f));
  const remove = (i) => setFaqs((p) => p.filter((_, idx) => idx !== i));
  const add = () => setFaqs((p) => [...p, { q: "", a: "" }]);
  const move = (i, dir) => {
    setFaqs((p) => {
      const next = [...p];
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    const clean = faqs.filter(f => f.q.trim() && f.a.trim());
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { key: "faqs", value: clean }, { headers: { "x-admin-token": token } });
      setFaqs(clean);
      toast({ title: "FAQs saved", description: "Your site now shows these questions." });
    } catch (err) {
      toast({ title: "Couldn't save", description: err.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="mt-8 text-[#D4A85B] text-xs uppercase tracking-[0.28em]">Loading…</div>;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80">Site FAQ</div>
          <p className="text-xs text-[#EDE3D0]/60 mt-1 max-w-lg">Edit, reorder, add, or remove the questions clients see in the FAQ section of your site. Changes are live once you save.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={add} data-testid="faq-add" className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A85B]/40 text-[#D4A85B] px-4 py-2 text-[10px] uppercase tracking-[0.24em] hover:bg-[#D4A85B] hover:text-[#0A0714] transition">
            <Plus size={12} /> Add question
          </button>
          <button onClick={save} disabled={saving} data-testid="faq-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A85B] text-[#0A0714] px-5 py-2 text-[10px] uppercase tracking-[0.24em] hover:bg-[#E9C77B] transition disabled:opacity-60">
            {saving ? "Saving…" : "Save all changes"}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {faqs.length === 0 && (
          <div className="rounded-2xl border border-[#D4A85B]/20 bg-[#12081E]/50 p-8 text-center text-sm text-[#EDE3D0]/60">
            No FAQs yet. Add your first question.
          </div>
        )}
        {faqs.map((f, i) => (
          <div key={i} data-testid={`faq-row-${i}`} className="rounded-2xl border border-[#D4A85B]/25 bg-[#12081E]/80 p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80">Question {i + 1}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-full border border-[#D4A85B]/30 text-[#EDE3D0] hover:border-[#D4A85B] hover:text-[#D4A85B] disabled:opacity-30 flex items-center justify-center transition"><ChevronUp size={12} /></button>
                <button onClick={() => move(i, 1)} disabled={i === faqs.length - 1} className="w-7 h-7 rounded-full border border-[#D4A85B]/30 text-[#EDE3D0] hover:border-[#D4A85B] hover:text-[#D4A85B] disabled:opacity-30 flex items-center justify-center transition"><ChevronDown size={12} /></button>
                <button onClick={() => remove(i)} data-testid={`faq-delete-${i}`} className="w-7 h-7 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/15 flex items-center justify-center transition"><X size={12} /></button>
              </div>
            </div>
            <input
              value={f.q}
              onChange={(e) => update(i, "q", e.target.value)}
              placeholder="Question"
              data-testid={`faq-q-${i}`}
              className="w-full bg-transparent border-b border-[#D4A85B]/25 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0] font-serif text-lg"
            />
            <textarea
              value={f.a}
              onChange={(e) => update(i, "a", e.target.value)}
              placeholder="Answer"
              rows={3}
              data-testid={`faq-a-${i}`}
              className="mt-3 w-full bg-[#0A0714]/60 border border-[#D4A85B]/20 focus:border-[#D4A85B] rounded-xl p-3 text-sm text-[#EDE3D0]/90 leading-relaxed outline-none resize-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminIntakes = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);

  const [tab, setTab] = useState("intakes"); // intakes | contacts | bookings | content
  const [intakes, setIntakes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    client_name: "",
    client_email: "",
    session_time: "",
    location_or_link: "",
    reschedule_link: "",
    service: "",
  });

  // Verify token on mount
  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/admin/verify`, { headers: { "x-admin-token": token } })
      .then(() => { setAuthed(true); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); })
      .finally(() => setChecking(false));
  }, [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { "x-admin-token": token };
      const [i, c, b] = await Promise.all([
        axios.get(`${API}/intakes`, { headers }),
        axios.get(`${API}/consultations`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
      ]);
      setIntakes(i.data || []);
      setContacts(c.data || []);
      setBookings(b.data || []);
    } catch (err) {
      toast({ title: "Couldn't load data", description: "Check your admin token and try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) fetchAll(); /* eslint-disable-next-line */ }, [authed]);

  const filteredIntakes = useMemo(() => {
    let list = [...intakes];
    if (urgencyFilter !== "all") list = list.filter(i => (i.urgencyLevel || "") === urgencyFilter);
    if (statusFilter !== "all") list = list.filter(i => (i.status || "new") === statusFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(i =>
        (i.fullName || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.concerns || []).join(" ").toLowerCase().includes(q) ||
        (i.story || "").toLowerCase().includes(q)
      );
    }
    // Sort by urgency then newest
    list.sort((a, b) => {
      const ua = URGENCY_ORDER[a.urgencyLevel] ?? 99;
      const ub = URGENCY_ORDER[b.urgencyLevel] ?? 99;
      if (ua !== ub) return ua - ub;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return list;
  }, [intakes, urgencyFilter, statusFilter, query]);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await axios.get(`${API}/admin/verify`, { headers: { "x-admin-token": tokenInput } });
      localStorage.setItem(TOKEN_KEY, tokenInput);
      setToken(tokenInput);
      setAuthed(true);
      toast({ title: "Welcome back, Devona" });
    } catch (err) {
      toast({ title: "That token didn't work", description: "Please try again." });
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setTokenInput(""); setAuthed(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/intakes/${id}`, { status }, { headers: { "x-admin-token": token } });
      setIntakes(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      toast({ title: `Marked as ${status}` });
    } catch (err) {
      toast({ title: "Update failed" });
    }
  };

  const createBooking = async (e) => {
    e.preventDefault();
    try {
      const iso = new Date(bookingForm.session_time).toISOString();
      await axios.post(`${API}/bookings`, {
        ...bookingForm,
        session_time: iso,
      }, { headers: { "x-admin-token": token } });
      toast({ title: "Booking added", description: "A reminder will send 24 hours before." });
      setShowBookingForm(false);
      setBookingForm({ client_name: "", client_email: "", session_time: "", location_or_link: "", reschedule_link: "", service: "" });
      fetchAll();
    } catch (err) {
      toast({ title: "Couldn't add booking", description: err.response?.data?.detail || "Please check the fields." });
    }
  };

  // ---------------- Render ----------------
  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0714] text-[#EDE3D0] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4A85B]" size={28} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0714] text-[#EDE3D0] flex items-center justify-center p-6">
        <form onSubmit={login} className="w-full max-w-md rounded-3xl border border-[#D4A85B]/40 bg-[#12081E] p-10 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -left-16 w-[240px] h-[240px] rounded-full bg-[#A36CFF]/25 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 w-[260px] h-[260px] rounded-full bg-[#D4A85B]/15 blur-[80px]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/85">
              <Lock size={12} /> Admin
            </div>
            <h1 className="font-serif text-3xl mt-4 text-[#EDE3D0]">Sign in to your <span className="italic text-[#D4A85B]">practice.</span></h1>
            <p className="mt-3 text-sm text-[#EDE3D0]/70">Enter your admin passphrase to view intakes, contacts, and upcoming sessions.</p>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Passphrase"
              data-testid="admin-token-input"
              className="mt-6 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-3 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
              autoFocus
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
            >
              Enter
            </button>
          </div>
        </form>
      </div>
    );
  }

  const stat = (label, n, tone = "gold") => (
    <div className="rounded-2xl border border-[#D4A85B]/25 bg-[#12081E]/80 p-5">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80">{label}</div>
      <div className={`mt-2 font-serif text-3xl ${tone === "gold" ? "text-[#D4A85B]" : "text-[#EDE3D0]"}`}>{n}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0714] text-[#EDE3D0]">
      {/* Header */}
      <header className="border-b border-[#D4A85B]/15 sticky top-0 z-40 bg-[#0A0714]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/80">Mindful Matters Healing</div>
            <div className="font-serif text-xl text-[#EDE3D0]">Practice <span className="italic text-[#D4A85B]">console</span></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchAll} className="text-[10px] uppercase tracking-[0.28em] text-[#EDE3D0]/70 hover:text-[#D4A85B] transition" data-testid="admin-refresh">Refresh</button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[#EDE3D0]/70 hover:text-[#D4A85B] transition" data-testid="admin-logout">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stat("Total intakes", intakes.length)}
          {stat("New / awaiting", intakes.filter(i => (i.status || "new") === "new").length)}
          {stat("Scheduled", intakes.filter(i => i.status === "scheduled").length, "cream")}
          {stat("Upcoming sessions", bookings.filter(b => !b.cancelled && new Date(b.session_time) > new Date()).length, "cream")}
        </div>

        {/* Tabs */}
        <div className="mt-10 flex items-center gap-2 border-b border-[#D4A85B]/15">
          {[
            { id: "intakes", label: "Intakes", count: intakes.length },
            { id: "contacts", label: "Contact messages", count: contacts.length },
            { id: "bookings", label: "Upcoming sessions", count: bookings.length },
            { id: "content", label: "Content", count: null },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-3 text-xs uppercase tracking-[0.28em] transition border-b-2 -mb-px ${
                tab === t.id ? "border-[#D4A85B] text-[#D4A85B]" : "border-transparent text-[#EDE3D0]/60 hover:text-[#EDE3D0]"
              }`}
            >
              {t.label} {t.count !== null && <span className="ml-1 text-[10px] opacity-60">({t.count})</span>}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-[#D4A85B]"><Loader2 className="animate-spin" size={16} /> <span className="text-xs uppercase tracking-[0.28em]">Loading</span></div>
        )}

        {/* INTAKES */}
        {tab === "intakes" && !loading && (
          <>
            {/* Filters */}
            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1 max-w-lg">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4A85B]/70" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email, concern, story…"
                  data-testid="admin-search-input"
                  className="w-full pl-9 pr-3 py-2.5 rounded-full bg-[#12081E] border border-[#D4A85B]/25 focus:border-[#D4A85B] outline-none text-sm text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                />
              </div>
              <select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)} data-testid="admin-urgency-filter" className="px-4 py-2.5 rounded-full bg-[#12081E] border border-[#D4A85B]/25 focus:border-[#D4A85B] outline-none text-xs uppercase tracking-[0.2em] text-[#EDE3D0]">
                <option value="all" className="bg-[#12081E]">All urgency</option>
                <option value="In crisis — need to speak sooner" className="bg-[#12081E]">In crisis</option>
                <option value="Feeling urgent" className="bg-[#12081E]">Urgent</option>
                <option value="Would love to start soon" className="bg-[#12081E]">Soon</option>
                <option value="Ready when you are" className="bg-[#12081E]">No rush</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter" className="px-4 py-2.5 rounded-full bg-[#12081E] border border-[#D4A85B]/25 focus:border-[#D4A85B] outline-none text-xs uppercase tracking-[0.2em] text-[#EDE3D0]">
                <option value="all" className="bg-[#12081E]">All statuses</option>
                <option value="new" className="bg-[#12081E]">New</option>
                <option value="responded" className="bg-[#12081E]">Responded</option>
                <option value="scheduled" className="bg-[#12081E]">Scheduled</option>
                <option value="closed" className="bg-[#12081E]">Closed</option>
              </select>
            </div>

            {/* List */}
            <div className="mt-6 space-y-3">
              {filteredIntakes.length === 0 && (
                <div className="rounded-2xl border border-[#D4A85B]/20 bg-[#12081E]/50 p-10 text-center text-sm text-[#EDE3D0]/60">
                  No intakes match these filters yet.
                </div>
              )}
              {filteredIntakes.map((i) => {
                const isOpen = openId === i.id;
                const urgencyKey = i.urgencyLevel || "";
                const isCrisis = urgencyKey.startsWith("In crisis");
                return (
                  <div key={i.id} data-testid={`intake-row-${i.id}`} className={`rounded-2xl border ${isCrisis ? "border-rose-500/50 bg-rose-500/[0.03]" : "border-[#D4A85B]/25 bg-[#12081E]/80"} transition`}>
                    <button onClick={() => setOpenId(isOpen ? null : i.id)} className="w-full p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 text-left">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-serif text-lg text-[#EDE3D0]">{i.fullName}</span>
                          <span className={`text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full border ${STATUS_STYLES[i.status || "new"]}`}>{i.status || "new"}</span>
                          {urgencyKey && (
                            <span className={`text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full border ${isCrisis ? "bg-rose-500/15 text-rose-300 border-rose-500/50" : "bg-[#D4A85B]/10 text-[#D4A85B] border-[#D4A85B]/30"}`}>
                              {isCrisis ? "In crisis" : urgencyKey.split(" ").slice(0, 3).join(" ")}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-[#EDE3D0]/60 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><Mail size={11} />{i.email}</span>
                          {i.phone && <span className="flex items-center gap-1"><Phone size={11} />{i.phone}</span>}
                          <span>{i.created_at ? new Date(i.created_at).toLocaleString() : ""}</span>
                        </div>
                        {(i.concerns || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {i.concerns.map(c => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4A85B]/10 text-[#D4A85B]/90 border border-[#D4A85B]/25">{c}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {i.status !== "scheduled" && (
                          <span
                            onClick={(e) => { e.stopPropagation(); updateStatus(i.id, "scheduled"); }}
                            data-testid={`mark-scheduled-${i.id}`}
                            className="cursor-pointer inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] hover:bg-emerald-500/25 transition"
                          >
                            <CheckCircle2 size={12} /> Mark scheduled
                          </span>
                        )}
                        {isOpen ? <ChevronUp size={16} className="text-[#D4A85B]" /> : <ChevronDown size={16} className="text-[#D4A85B]" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-[#D4A85B]/15 p-6 md:p-8 grid md:grid-cols-2 gap-6 bg-[#0A0714]/60">
                        <Field label="Pronouns" value={i.pronouns} />
                        <Field label="Date of birth" value={i.dob} />
                        <Field label="Location" value={i.address} />
                        <Field label="Emergency contact" value={[i.emergencyName, i.emergencyPhone].filter(Boolean).join(" — ")} />
                        <Field label="Service interest" value={i.service} />
                        <Field label="Availability" value={i.availability} />
                        <Field label="Heard from" value={i.heardFrom} />
                        <Field label="Currently in therapy" value={i.inTherapy} />
                        <Field label="On medication" value={i.onMedication} />
                        <Field label="Medications" value={i.medicationList} />
                        <Field label="Past treatment" value={i.pastTreatment} />
                        <Field label="Diagnoses" value={i.diagnoses} />
                        <Field label="Substance use" value={i.substanceUse} />
                        <Field label="Safety concerns" value={i.safetyConcerns} />
                        <div className="md:col-span-2"><Field label="Their story" value={i.story} /></div>
                        <div className="md:col-span-2"><Field label="Goals" value={i.goals} /></div>
                        <div className="md:col-span-2"><Field label="What's helped before" value={i.pastHelped} /></div>
                        <Field label="Signature" value={i.signature} />
                        <Field label="Signature date" value={i.signatureDate} />
                        <div className="md:col-span-2 pt-4 border-t border-[#D4A85B]/15">
                          <NotesEditor intakeId={i.id} initial={i.private_notes || ""} token={token} onSaved={(v) => setIntakes(prev => prev.map(x => x.id === i.id ? { ...x, private_notes: v } : x))} />
                        </div>
                        <div className="md:col-span-2 pt-3 border-t border-[#D4A85B]/15 flex items-center gap-2 flex-wrap">
                          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/80 mr-2">Update status:</div>
                          {["new", "responded", "scheduled", "closed"].map(s => (
                            <button
                              key={s}
                              onClick={() => updateStatus(i.id, s)}
                              className={`text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full border transition ${
                                (i.status || "new") === s ? STATUS_STYLES[s] : "border-[#D4A85B]/25 text-[#EDE3D0]/70 hover:border-[#D4A85B] hover:text-[#D4A85B]"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                          <a
                            href={`mailto:${i.email}`}
                            className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#D4A85B] hover:text-[#E9C77B]"
                          >
                            <Mail size={12} /> Reply by email
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && !loading && (
          <div className="mt-8 space-y-3">
            {contacts.length === 0 && (
              <div className="rounded-2xl border border-[#D4A85B]/20 bg-[#12081E]/50 p-10 text-center text-sm text-[#EDE3D0]/60">
                No contact messages yet.
              </div>
            )}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="rounded-2xl border border-[#D4A85B]/25 bg-[#12081E]/80 p-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg text-[#EDE3D0]">{c.name}</span>
                  <span className="text-xs text-[#EDE3D0]/60">· {c.email}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-[0.24em] text-[#D4A85B]/70">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                </div>
                {c.service && <div className="mt-1 text-xs text-[#D4A85B]/80">Interested in: {c.service}</div>}
                {c.message && <p className="mt-3 text-sm text-[#EDE3D0]/85 whitespace-pre-wrap">{c.message}</p>}
                <a href={`mailto:${c.email}`} className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#D4A85B] hover:text-[#E9C77B]">
                  <Mail size={12} /> Reply by email
                </a>
              </div>
            ))}
          </div>
        )}

        {/* BOOKINGS */}
        {tab === "bookings" && !loading && (
          <>
            <div className="mt-8 flex items-center justify-between gap-3">
              <p className="text-xs text-[#EDE3D0]/60 max-w-xl">
                Auto-reminders send 24 hours before each session. Calendly webhooks fill this list automatically once connected — for now you can add sessions manually.
              </p>
              <button
                onClick={() => setShowBookingForm(true)}
                data-testid="add-booking-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A85B] text-[#0A0714] px-5 py-2.5 text-xs uppercase tracking-[0.24em] hover:bg-[#E9C77B] transition"
              >
                <Plus size={13} /> Add session
              </button>
            </div>

            <div className="mt-6 space-y-3">
              {bookings.length === 0 && (
                <div className="rounded-2xl border border-[#D4A85B]/20 bg-[#12081E]/50 p-10 text-center text-sm text-[#EDE3D0]/60">
                  No sessions on the calendar yet.
                </div>
              )}
              {bookings.map((b) => {
                const dt = new Date(b.session_time);
                const isPast = dt < new Date();
                return (
                  <div key={b.id} data-testid={`booking-row-${b.id}`} className={`rounded-2xl border ${b.cancelled ? "border-rose-500/40 bg-rose-500/[0.03]" : isPast ? "border-white/10 bg-[#12081E]/40" : "border-[#D4A85B]/25 bg-[#12081E]/80"} p-5`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Calendar size={14} className="text-[#D4A85B]" />
                          <span className="font-serif text-lg text-[#EDE3D0]">{b.client_name}</span>
                          {b.cancelled && <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/40">Cancelled</span>}
                          {b.reminder_sent && !b.cancelled && <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Reminder sent</span>}
                        </div>
                        <div className="mt-1 text-xs text-[#EDE3D0]/70">{dt.toLocaleString()} · {b.client_email}</div>
                        {b.service && <div className="mt-1 text-xs text-[#D4A85B]/80">{b.service}</div>}
                        {b.location_or_link && <div className="mt-1 text-xs text-[#EDE3D0]/60 truncate">Location: {b.location_or_link}</div>}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4A85B]/70">{b.source}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {showBookingForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog">
                <div className="absolute inset-0 bg-[#0A0714]/90 backdrop-blur-sm" onClick={() => setShowBookingForm(false)} />
                <form onSubmit={createBooking} className="relative w-full max-w-lg rounded-3xl border border-[#D4A85B]/40 bg-[#12081E] p-8">
                  <button type="button" onClick={() => setShowBookingForm(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full border border-[#D4A85B]/40 flex items-center justify-center hover:bg-[#D4A85B] hover:text-[#0A0714] transition">
                    <X size={14} />
                  </button>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]">Add session</div>
                  <h3 className="font-serif text-2xl mt-2 text-[#EDE3D0]">Schedule a <span className="italic text-[#D4A85B]">session</span></h3>
                  <p className="mt-2 text-xs text-[#EDE3D0]/60">A reminder email will send automatically 24 hours before the start time.</p>

                  <div className="mt-6 space-y-4">
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Client name *</label>
                      <input required value={bookingForm.client_name} onChange={e => setBookingForm({...bookingForm, client_name: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" data-testid="booking-name" /></div>
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Client email *</label>
                      <input required type="email" value={bookingForm.client_email} onChange={e => setBookingForm({...bookingForm, client_email: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" data-testid="booking-email" /></div>
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Session time *</label>
                      <input required type="datetime-local" value={bookingForm.session_time} onChange={e => setBookingForm({...bookingForm, session_time: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" data-testid="booking-time" /></div>
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Service (optional)</label>
                      <input value={bookingForm.service} onChange={e => setBookingForm({...bookingForm, service: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" placeholder="Healing Journey Session" /></div>
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Location or Zoom link</label>
                      <input value={bookingForm.location_or_link} onChange={e => setBookingForm({...bookingForm, location_or_link: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" placeholder="https://zoom.us/j/..." /></div>
                    <div><label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Reschedule link (optional)</label>
                      <input value={bookingForm.reschedule_link} onChange={e => setBookingForm({...bookingForm, reschedule_link: e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2 text-[#EDE3D0]" placeholder="https://calendly.com/..." /></div>
                  </div>
                  <button type="submit" data-testid="booking-submit" className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-6 py-3 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition">
                    Save session
                  </button>
                </form>
              </div>
            )}
          </>
        )}

        {tab === "content" && !loading && (
          <FaqEditor token={token} />
        )}
      </main>
    </div>
  );
};

export default AdminIntakes;
